import { supabase } from '@/integrations/supabase/client';
import {
  canUseSupabaseTransform,
  getOptimizedImageUrl,
  getOptimizedStoragePath,
  getThumbnailUrl,
  parseSupabasePublicUrl,
  THUMBNAIL_HEIGHT,
  THUMBNAIL_QUALITY,
  THUMBNAIL_WIDTH,
} from './imageUtils';

/**
 * Bakes a small WebP thumbnail for an original Supabase storage image and
 * stores it as a permanent static file at the deterministic `optimized/...`
 * path so it can be served directly from the object CDN (fast, like a CDN
 * thumbnail) instead of being transformed on-the-fly on every page view.
 *
 * The transformation itself is produced once via Supabase's render endpoint,
 * then the resulting bytes are uploaded back to storage.
 *
 * Returns the public URL of the baked thumbnail, or null when the image cannot
 * be transformed (non-Supabase URL or unsupported gif/svg).
 */
export const generateAndStoreThumbnail = async (
  originalPublicUrl: string,
): Promise<string | null> => {
  if (!canUseSupabaseTransform(originalPublicUrl)) return null;

  const parsed = parseSupabasePublicUrl(originalPublicUrl);
  if (!parsed) return null;

  const renderUrl = getOptimizedImageUrl(originalPublicUrl, {
    width: THUMBNAIL_WIDTH,
    height: THUMBNAIL_HEIGHT,
    quality: THUMBNAIL_QUALITY,
    format: 'webp',
    resize: 'cover',
  });

  const response = await fetch(renderUrl);
  if (!response.ok) {
    throw new Error(`Failed to render thumbnail (${response.status})`);
  }

  const blob = await response.blob();
  const optimizedPath = getOptimizedStoragePath(parsed.path);

  const { error } = await supabase.storage
    .from(parsed.bucket)
    .upload(optimizedPath, blob, { contentType: 'image/webp', upsert: true });

  if (error) throw error;

  return getThumbnailUrl(originalPublicUrl);
};

/**
 * Checks whether a baked thumbnail already exists for the given original URL.
 * Used by the backfill tool to skip images that are already optimized.
 */
export const thumbnailExists = async (originalPublicUrl: string): Promise<boolean> => {
  const thumbnailUrl = getThumbnailUrl(originalPublicUrl);
  if (!thumbnailUrl || thumbnailUrl === originalPublicUrl) return false;

  try {
    const response = await fetch(thumbnailUrl, { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
};
